import React from 'react';
import { AppBar, Toolbar, CssBaseline, styled, IconButton, InputBase, Box, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { LAYOUT_CONFIG, LayoutMode } from '../../../../constants/layout';
import { useLanguage } from '../../../../context/LanguageContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import LanguageSelector from './LanguageSelector';
import NotificationBell from './NotificationBell';
import SettingsMenu from './SettingsMenu';
import WorkspaceSelector from '../WorkspaceSelector';

const AppBarStyled = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'layoutMode' && prop !== 'topbarTheme',
})<{ open?: boolean; layoutMode?: LayoutMode; topbarTheme?: 'light' | 'dark' }>(({ theme, open, layoutMode, topbarTheme }) => {
    const sidebarWidth = open ? LAYOUT_CONFIG.drawerWidth : LAYOUT_CONFIG.collapsedWidth;
    const isVertical = layoutMode === 'vertical';
    const isDark = topbarTheme === 'dark';
    
    return {
        height: LAYOUT_CONFIG.headerHeight,
        // Fond de la barre haute en thème sombre : teinte propre au topbar, absente de la palette
        backgroundColor: isDark ? '#2c313a' : theme.palette.background.paper,
        boxShadow: '0 0.75rem 1.5rem rgba(18, 38, 63, 0.03)',
        left: isVertical ? sidebarWidth : 0,
        width: isVertical ? `calc(100% - ${sidebarWidth}px)` : '100%',
        transition: LAYOUT_CONFIG.transition,
    };
});

const SearchWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginLeft: 16,
    '& .search-icon': {
        position: 'absolute',
        left: 14,
        top: '50%',
        transform: 'translateY(-50%)',
        color: theme.palette.text.secondary,
        fontSize: 18,
        pointerEvents: 'none',
        zIndex: 1
    }
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 30,
    padding: '8px 16px 8px 40px',
    width: 300,
    fontSize: 14,
    transition: 'all 0.2s ease',
    '&:focus-within': {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
    }
}));

const LogoLink = styled(Link)({
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
});

interface HeaderProps {
    open: boolean;
    onToggle: () => void;
    layoutMode?: LayoutMode;
    onLayoutModeChange?: (mode: LayoutMode) => void;
}

const toggleFullscreen = () => {
    const doc = document as any;
    const docEl = document.documentElement as any;
    
    const isFullscreen = document.fullscreenElement || doc.mozFullScreenElement || doc.webkitFullscreenElement;
    
    if (!isFullscreen) {
        const requestMethod = docEl.requestFullscreen || docEl.mozRequestFullScreen || docEl.webkitRequestFullscreen;
        requestMethod?.call(docEl);
    } else {
        const exitMethod = doc.exitFullscreen || doc.mozCancelFullScreen || doc.webkitExitFullscreen;
        exitMethod?.call(doc);
    }
};

const LayoutModeButton: React.FC<{
    mode: LayoutMode;
    currentMode: LayoutMode;
    icon: string;
    title: string;
    onClick: (mode: LayoutMode) => void;
}> = ({ mode, currentMode, icon, title, onClick }) => {
    const isActive = currentMode === mode;
    return (
        <Tooltip title={title}>
            <IconButton 
                onClick={() => onClick(mode)}
                sx={{
                    color: isActive ? 'primary.main' : 'text.primary',
                    backgroundColor: (theme) => isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: (theme) => alpha(isActive ? theme.palette.primary.main : theme.palette.text.primary, isActive ? 0.15 : 0.1),
                        transform: 'translateY(-2px)'
                    }
                }}
            >
                <i className={icon} style={{ fontSize: 20 }} />
            </IconButton>
        </Tooltip>
    );
};

const Header: React.FC<HeaderProps> = ({ open, onToggle, layoutMode = 'vertical', onLayoutModeChange }) => {
    const { t } = useLanguage();
    const theme = useTheme();
    const { topbarTheme } = useLayoutPreferences();
    const isDark = topbarTheme === 'dark';
    const iconColor = isDark ? theme.palette.grey[400] : theme.palette.text.primary;

    return (
        <>
            <CssBaseline />
            <AppBarStyled position="fixed" open={open} layoutMode={layoutMode} topbarTheme={topbarTheme}>
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: `${LAYOUT_CONFIG.headerHeight}px !important` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {layoutMode === 'vertical' ? (
                            <IconButton onClick={onToggle} sx={{ color: iconColor }}>
                                <i className="bx bx-menu" style={{ fontSize: 25 }} />
                            </IconButton>
                        ) : (
                            <LogoLink to="/dashboard">
                                <Box sx={{ width: 150, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <img
                                        src={isDark ? "/logo_login.png" : "/logo_violet.png"}
                                        alt="BeOne"
                                        style={{ maxHeight: 40, maxWidth: 150, width: 'auto', height: 'auto', display: 'block', objectFit: 'contain' }}
                                    />
                                </Box>
                            </LogoLink>
                        )}

                        <SearchWrapper>
                            <i className="bx bx-search-alt search-icon" style={{ color: isDark ? theme.palette.grey[400] : theme.palette.text.secondary }} />
                            <SearchInput
                                placeholder={t('common.search')}
                                sx={{
                                    // Champ de recherche en thème sombre : teintes propres au topbar, absentes de la palette
                                    backgroundColor: isDark ? '#3a3f4b' : 'grey.50',
                                    border: `1px solid ${isDark ? '#4e5562' : theme.palette.grey[200]}`,
                                    color: isDark ? 'grey.400' : 'inherit',
                                }}
                            />
                        </SearchWrapper>

                        <WorkspaceSelector />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {onLayoutModeChange && (
                            <>
                                <LayoutModeButton
                                    mode="vertical"
                                    currentMode={layoutMode}
                                    icon="bx bx-sidebar"
                                    title={t('header.verticalMenu')}
                                    onClick={onLayoutModeChange}
                                />
                                <LayoutModeButton
                                    mode="horizontal"
                                    currentMode={layoutMode}
                                    icon="bx bx-menu"
                                    title={t('header.horizontalMenu')}
                                    onClick={onLayoutModeChange}
                                />
                            </>
                        )}
                        
                        <LanguageSelector />

                        <NotificationBell />
                        
                        <SettingsMenu />
                        
                        <Tooltip title={t('header.fullscreen')}>
                            <IconButton onClick={toggleFullscreen} sx={{ color: iconColor }}>
                                <i className="bx bx-fullscreen" style={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>
            </AppBarStyled>
        </>
    );
};

export default Header;
